import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "../context/auth-context";
import { useUserSubscriptions } from "@/hooks/use-user-subscriptions";
import { Loader2, AlertCircle, Shield, CreditCard } from "lucide-react";
import { AuthLayout } from "./auth-layout";

export function SelectSubscriptionForm() {
  const navigate = useNavigate();
  const {
    selectedSubscriptionId,
    setSelectedSubscriptionId,
    setSelectedCompanyId,
  } = useAuth();
  const { subscriptions, isLoading: loading, isError } = useUserSubscriptions();
  const [selected, setSelected] = useState<string | null>(null);

  const error = isError ? "No fue posible cargar suscripciones" : null;

  useEffect(() => {
    if (selectedSubscriptionId) {
      if (selectedSubscriptionId === "SYSTEM") {
        navigate("/", { replace: true });
      } else {
        navigate("/select-company", { replace: true });
      }
    }
  }, [selectedSubscriptionId, navigate]);

  useEffect(() => {
    if (!selectedSubscriptionId) {
      try {
        const stored = localStorage.getItem("selectedSubscriptionId");
        if (stored) {
          setSelectedSubscriptionId(stored);
        }
      } catch (restoreError) {
        console.error(
          "Error restaurando suscripción desde localStorage:",
          restoreError,
        );
      }
    }
  }, [selectedSubscriptionId, setSelectedSubscriptionId]);

  const canConfirm = Boolean(selected);

  const handleConfirm = () => {
    if (!selected) return;
    setSelectedSubscriptionId(selected);
    setSelectedCompanyId(null);

    if (selected === "SYSTEM") {
      navigate("/", { replace: true });
    } else {
      navigate("/select-company", { replace: true });
    }
  };

  return (
    <AuthLayout>
      <form onSubmit={(e) => e.preventDefault()}>
        <FieldGroup>
          <div className="flex flex-col items-center gap-2 text-center">
            <h1 className="text-2xl font-bold">Selecciona una suscripción</h1>
            <p className="text-sm text-muted-foreground">
              Elige la suscripción con la que deseas trabajar en esta sesión
            </p>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="mb-3 h-8 w-8 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">
                Cargando suscripciones...
              </span>
            </div>
          ) : null}

          {error ? (
            <div className="rounded-md bg-destructive/10 p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            </div>
          ) : null}

          {!loading && !error && subscriptions.length === 0 ? (
            <div className="py-8 text-center">
              <CreditCard className="mx-auto mb-3 h-12 w-12 text-muted-foreground opacity-50" />
              <p className="mb-1 text-sm font-medium text-muted-foreground">
                No tienes acceso a ninguna suscripción
              </p>
              <p className="text-xs text-muted-foreground">
                Contacta al administrador para obtener acceso
              </p>
            </div>
          ) : null}

          {!loading && !error && subscriptions.length > 0 ? (
            <>
              <Field>
                <FieldLabel htmlFor="subscription-select">
                  Suscripción
                </FieldLabel>
                <Select
                  value={selected ?? ""}
                  onValueChange={(value) => setSelected(value || null)}
                >
                  <SelectTrigger id="subscription-select" className="w-full">
                    <SelectValue placeholder="Selecciona una suscripción..." />
                  </SelectTrigger>
                  <SelectContent>
                    {subscriptions.some((s) => s.isSystem) ? (
                      <>
                        <div className="relative py-2">
                          <div className="absolute inset-0 flex items-center px-2">
                            <span className="w-full border-t" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-background px-2 text-muted-foreground">
                              Plataforma
                            </span>
                          </div>
                        </div>
                        {subscriptions
                          .filter((s) => s.isSystem)
                          .map((sub) => (
                            <SelectItem key={sub.id} value={sub.id}>
                              <div className="flex items-center gap-2">
                                <Shield className="h-4 w-4 shrink-0 text-amber-500" />
                                <span>Plataforma</span>
                                <span className="ml-auto text-xs text-muted-foreground">
                                  Administrar datos globales del sistema
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                      </>
                    ) : null}
                    {subscriptions.some((s) => !s.isSystem) ? (
                      <>
                        <div className="relative py-2">
                          <div className="absolute inset-0 flex items-center px-2">
                            <span className="w-full border-t" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-background px-2 text-muted-foreground">
                              Suscripciones
                            </span>
                          </div>
                        </div>
                        {subscriptions
                          .filter((s) => s.active !== false && !s.isSystem)
                          .map((sub) => (
                            <SelectItem key={sub.id} value={sub.id}>
                              <div className="flex items-center gap-2">
                                <span
                                  className={`h-2.5 w-2.5 shrink-0 animate-pulse rounded-full ${
                                    sub.status === "active"
                                      ? "bg-green-500"
                                      : "bg-red-500"
                                  }`}
                                />
                                <span>{sub.description || sub.planName}</span>
                                <span className="ml-auto text-xs text-muted-foreground">
                                  {sub.planId
                                    ? `(${sub.planId.slice(0, 8)}...)`
                                    : ""}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                      </>
                    ) : null}
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <Button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!canConfirm}
                  className="w-full"
                >
                  Continuar
                </Button>
              </Field>
            </>
          ) : null}
        </FieldGroup>
      </form>
    </AuthLayout>
  );
}
